<template>
    <button
        ref="button"
        class="button-circle"
        type="button"
        :style="styles"
        :class="classes"
        @click="handleClick"
    >
        <template v-if="type === 'default'">
            <slot />
        </template>
        <template v-if="type === 'wrapper'">
            <div class="button-circle__content" :style="stylesWrapper">
                <slot />
            </div>
        </template>
    </button>
</template>

<script>
import { SVG } from '@svgdotjs/svg.js';

export default {
    props: {
        type: { type: String, default: 'default' },
        size: { type: Number, default: 31 },
        sizeWrapper: { type: Number, default: 16 },
        backgroundColorWrapper: { type: String, default: '#000000' },
        strokeColor: { type: String, default: '#DBDBDB' },
        strokeWidth: { type: Number, default: 1 },
        strokeOpacity: { type: Number, default: 1 },
        strokeColorProgress: { type: String, default: '#1C97F3' },
        strokeWidthProgress: { type: Number, default: 1 },
        strokeOpacityProgress: { type: Number, default: 1 },
        strokeColorLevel: { type: String, default: '#54d475' },
        color: { type: String, default: '#4A4A4A' },
        fontSize: { type: Number, default: 10 },
        progress: { type: Number, default: 0 },
        progressMax: { type: Number, default: 100 },
        active: { type: Boolean, default: false },
        hover: { type: Boolean, default: true },
        levelEnable: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        levelValue: { type: Number, default: 0 },
    },

    emits: ['handleClick'],

    data() {
        return {
            svgDraw: null,
        };
    },

    computed: {
        percentMax() {
            const radius = this.svgDrawCircle.attr('r');
            const cos = Math.PI * (radius * 2);
            let value = this.progressMax;

            if (value < 0) {
                value = 0;
            }
            if (value > 100) {
                value = 100;
            }

            return ((100 - value) / 100) * cos;
        },

        percent() {
            const radius = this.svgDrawCircleProgress.attr('r');
            const cos = Math.PI * (radius * 2);
            let value = 0;

            if (this.type === 'wrapper') {
                value = this.progress;
            } else {
                value = this.active ? this.progress : 100;
            }

            if (value < 0) {
                value = 0;
            }
            if (value > 100) {
                value = 100;
            }

            return ((100 - value) / 100) * cos;
        },

        styles() {
            const styles = {
                width: `${this.size}px`,
                height: `${this.size}px`,
                fontSize: `${this.fontSize}px`,
            };

            if (this.color) {
                styles.color = this.color;
            }

            return styles;
        },

        stylesWrapper() {
            return {
                width: `${this.sizeWrapper}px`,
                height: `${this.sizeWrapper}px`,
                backgroundColor: this.backgroundColorWrapper,
            };
        },

        classes() {
            return {
                'button-circle--active': this.active,
                'button-circle--hover': this.hover,
                'button-circle--disabled': this.disabled,
            };
        },
    },

    watch: {
        levelValue(percent) {
            const sizeMin = this.sizeWrapper;
            const sizeMax = this.size;
            const sizePercent = (sizeMax - sizeMin) * (percent / 100);
            const result = sizeMin + sizePercent;

            this.svgDrawCircleLevel.size(result);
        },

        progress() {
            this.initPercent();
        },

        active(status) {
            this.initPercent();

            if (this.type === 'default') {
                if (status) {
                    this.svgDrawCircleProgress.attr('visibility', 'visible');
                } else {
                    this.svgDrawCircleProgress.attr('visibility', 'hidden');
                }
            }
        },
    },

    methods: {
        handleClick() {
            this.$emit('handleClick');
        },

        init() {
            this.initDraw();
            this.initPercent();
        },

        initDraw() {
            this.svgDraw = SVG().addTo(this.$refs.button).size('100%', '100%');

            if (this.levelEnable) {
                this.drawLevel();
            }

            if (this.type === 'default') {
                this.drawProgress();
            }

            if (this.type === 'wrapper') {
                this.drawProgressWrapper();
            }
        },

        drawProgress() {
            this.svgDrawCircle = this.svgDraw
                .circle(this.size)
                .attr('r', this.size / 2 - this.strokeWidth / 2)
                .move(this.strokeWidth / 2, this.strokeWidth / 2)
                .fill('transparent')
                .stroke({
                    color: this.strokeColor,
                    opacity: this.strokeOpacity,
                    width: this.strokeWidth,
                });

            this.svgDrawCircleProgress = this.svgDraw
                .circle(this.size)
                .attr('r', this.size / 2 - this.strokeWidthProgress / 2)
                .move(this.strokeWidthProgress / 2, this.strokeWidthProgress / 2)
                .fill('transparent')
                .stroke({
                    color: this.strokeColorProgress,
                    opacity: this.strokeOpacityProgress,
                    width: this.strokeWidthProgress,
                });

            const radius = this.svgDrawCircleProgress.attr('r');
            const cos = Math.PI * (radius * 2);

            this.svgDrawCircleProgress
                .attr('stroke-dasharray', cos)
                .attr('stroke-dashoffset', 0)
                .attr('visibility', 'hidden')
                .css('stroke-dashoffset', cos)
                .rotate(-90);
        },

        drawProgressWrapper() {
            this.svgDrawCircle = this.svgDraw
                .circle(this.size)
                .attr('r', this.size / 2 - this.strokeWidth / 2)
                .move(this.strokeWidth / 2, this.strokeWidth / 2)
                .fill('transparent')
                .stroke({
                    color: this.strokeColor,
                    opacity: this.strokeOpacity,
                    width: this.strokeWidth,
                });

            const radius = this.svgDrawCircle.attr('r');
            const cos = Math.PI * (radius * 2);

            this.svgDrawCircle
                .attr('stroke-dasharray', cos)
                .attr('stroke-dashoffset', 0)
                .attr('stroke-linecap', 'round')
                .css('stroke-dashoffset', cos)
                .rotate(-90);

            this.svgDrawCircleProgress = this.svgDraw
                .circle(this.size)
                .attr('r', this.size / 2 - this.strokeWidthProgress / 2)
                .move(this.strokeWidthProgress / 2, this.strokeWidthProgress / 2)
                .fill('transparent')
                .stroke({
                    color: this.strokeColorProgress,
                    opacity: this.strokeOpacityProgress,
                    width: this.strokeWidthProgress,
                });

            const radiusProgress = this.svgDrawCircleProgress.attr('r');
            const cosProgress = Math.PI * (radiusProgress * 2);

            this.svgDrawCircleProgress
                .attr('stroke-dasharray', cosProgress)
                .attr('stroke-dashoffset', 0)
                .attr('stroke-linecap', 'round')
                .css('stroke-dashoffset', cosProgress)
                .rotate(-90);
        },

        drawLevel() {
            this.svgDrawCircleLevel = this.svgDraw
                .circle(this.sizeWrapper)
                .move(this.size / 2 - this.sizeWrapper / 2, this.size / 2 - this.sizeWrapper / 2)
                .fill(this.strokeColorLevel);
        },

        initPercent() {
            if (this.type === 'default') {
                this.svgDrawCircleProgress.css('stroke-dashoffset', this.percent);
            }

            if (this.type === 'wrapper') {
                this.svgDrawCircle.css('stroke-dashoffset', this.percentMax);
                this.svgDrawCircleProgress.css('stroke-dashoffset', this.percent);
            }
        },
    },

    mounted() {
        this.init();
    },
};
</script>
